import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ViewContext } from "components/lib";
import { ModalContext } from "app/app";

// Components
import { IconedButton } from "components/IconedButton";
import CustomLoader from "components/CustomLoader";
import { useAPI } from "components/lib";

// Local components
import EndorsementDetails from './components/EndorsementDetails';
import EndorsementMessages from './components/EndorsementMessages';

import styles from './styles.module.scss';

export default function EndorsementsViewV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);
  const user = useAPI('/api/user');

  const [endorsementData, setEndorsementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageDescription, setMessageDescription] = useState('');

  const fetchEndorsement = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/endorsement/${id}`);
      setEndorsementData(response.data.data);
    } catch (err) {
      setError(err);
      context.notification.show(
        err.message || "Error fetching endorsement",
        "error",
        true,
        "toast",
        "x-circle"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchEndorsement();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleUpdateEndorsementChange = async (changeId, solved) => {
    try {
      const updatedChanges = endorsementData.changes.map((change) =>
        change.value === changeId ? { ...change, solved } : change
      );

      const response = await axios.put(`/api/endorsement/${id}`, {
        changes: updatedChanges,
      });

      if (response.status !== 200) {
        throw new Error('Failed to update endorsement change');
      }

      setEndorsementData((prevData) => ({
        ...prevData,
        changes: updatedChanges,
      }));
      fetchEndorsement();
    } catch (error) {
      context.notification.show(
        'There was a problem when updating the change.',
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  };

  const handleUpdateField = async (fieldName, fieldValue) => {
    try {
      const response = await axios.put(`/api/endorsement/${id}`, {
        [fieldName]: fieldValue
      });

      if (response.data.success) {
        context.notification.show(
          'Field updated successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );
        fetchEndorsement();
      }
    } catch (error) {
      let errorMessage = error.message || `Failed to update ${fieldName}`;

      if (error.response && error.response.status === 403) {
        errorMessage = error.response.data.message || 'This endorsement is locked from editing';
      }

      context.notification.show(
        errorMessage,
        'error',
        true,
        'toast',
        'x-circle'
      );
    }
  };

  const handleSubmit = async (values) => {
    setSending(true);

    try {
      const formData = new FormData();
      formData.append('sender', 'endorsements@coverbadger.com');
      formData.append('receiver', endorsementData.agent_email);
      formData.append('title', values.title);
      formData.append('description', values.description);

      [...selectedFiles].forEach((file) => {
        formData.append('documents', file, file.name);
      });

      const response = await axios.put(
        `/api/endorsement/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        context.notification.show(
          'Message sent successfully!',
          'success',
          true,
          'toast',
          'check-circle'
        );
        setSelectedFiles([]);
        fetchEndorsement();
      }
    } catch (error) {
      let errorMessage = error.message || 'Failed to send message';

      if (error.response && error.response.status === 403) {
        errorMessage = error.response.data.message || 'This endorsement is locked from editing';
      }

      context.notification.show(
        errorMessage,
        'error',
        true,
        'toast',
        'x-circle'
      );
    } finally {
      setSending(false);
    }
  };

  if (loading || user.loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <CustomLoader />
      </div>
    );
  }

  const disableChange = endorsementData.status === 'complete' ||
    endorsementData.status === 'bound' ||
    endorsementData.status === 'permanently_delete_ticket';

  if (error) {
    return <div>Error loading endorsement: {error.message}</div>;
  }

  if (!endorsementData) {
    return <div>No endorsement data available</div>;
  }

  return (
    <>
      {!endorsementData ? (
        <div>
          <div className="w-full flex items-center justify-left">
            <IconedButton
              size="130px"
              color="#d32425"
              textColor="white"
              icon="arrow-left"
              text="Go back"
              action={() => navigate(`/admin/endorsements`)}
            />
          </div>
          <div className="flex items-center">Endorsement not found</div>
        </div>
      ) : (
        <>
          <div className="w-full flex items-center justify-left">
            <IconedButton
              size="130px"
              color="#d32425"
              textColor="white"
              icon="arrow-left"
              text="Go back"
              action={() => navigate(`/admin/endorsements`)}
            />
          </div>
          <main className="flex-1 flex flex-col md:flex-row gap-4 max-h-full mt-2">
            {/* left */}
            <EndorsementDetails
              endorsementData={endorsementData}
              handleUpdateEndorsementChange={handleUpdateEndorsementChange}
              handleUpdateField={handleUpdateField}
              setModal={setModal}
              styles={styles}
              fetchEndorsement={fetchEndorsement}
              disableChange={disableChange}
            />

            {/* right */}
            <EndorsementMessages
              endorsementData={endorsementData}
              handleSubmit={handleSubmit}
              sending={sending}
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              messageTitle={messageTitle}
              setMessageTitle={setMessageTitle}
              messageDescription={messageDescription}
              setMessageDescription={setMessageDescription}
              setModal={setModal}
              disableChange={disableChange}
            />
          </main>
        </>
      )}
    </>
  );
}
